import { zodResolver } from '@hookform/resolvers/zod'
import { Save } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CardActions,
  Divider,
  Fab,
  Grid2 as Grid,
  LinearProgress,
  TextField,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { format, formatRFC3339, isValid, parse, parseISO } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { patchDevice } from '@/api/Device'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import useDebounce from '@/hooks/Debounce'
import type {
  Device,
  ZklRc,
  Rcs,
  Rss,
  Rdi,
  ZklRcAftc,
} from '@/pages/Device/Device'
import ImeiChangeDialog from '@/pages/Device/ImeiChangeDialog'
import UidChangeDialog from '@/pages/Device/UidChangeDialog'
import {
  ZKL_3000_RC,
  ZKL_3000_RCC,
  ZKL_3000,
} from '@/pages/Device/SupportedTypes'
import { labelSx } from '@/styles/MuiComponentStyles'

type UpdateFormProps = {
  device?: Device
  onUpdated?: (device: Device) => void
}

export default function UpdateForm({
  device,
  onUpdated,
}: Readonly<UpdateFormProps>) {
  const { enqueueSnackbar } = useSnackbar()
  const [t] = useTranslation()
  const [confirmationDialog, setConfirmationDialog] = useState(false)
  const [showProgressIndicator, setShowProgressIndicator] = useState(false)
  const debouncedShowProgressIndicator = useDebounce(showProgressIndicator, 300)
  const [imeiOpen, setImeiOpen] = useState(false)
  const [uidOpen, setUidOpen] = useState(false)

  const schema = z.object({
    serial: z.string().min(1, { message: t('common:form.required') }),
    productionDate: z.string().optional(),
    productionNr: z.string().optional(),
    pcbSerials: z.record(z.string(), z.string().optional()),
    writtenOff: z.boolean(),
  })

  type FormValues = z.infer<typeof schema>

  let pcbSerials: (
    | keyof ZklRc['pcbSerials']
    | keyof ZklRcAftc['pcbSerials']
    | keyof Rdi['pcbSerials']
    | keyof Rss['pcbSerials']
    | keyof Rcs['pcbSerials']
  )[] = []

  if (device) {
    switch (device.type) {
      case 'zkl-3000-rc-aftc':
        pcbSerials = [
          'batteryBoardPCB',
          'ncmPCB',
          'switchPCB',
          'mainPCB',
          'aftcPCB',
        ]
        break
      case 'zkl-3000-rc':
      case 'zkl-3000-rcc':
        pcbSerials = ['batteryBoardPCB', 'ncmPCB', 'switchPCB', 'mainPCB']
        break
      case 'rdi':
        pcbSerials = [
          'batteryPackPCB',
          'ncmPCB',
          'mainPCB',
          'motor',
          'ledPCB',
          'microswitchSerialPCB',
          'potentiometerSerialPCB',
        ]
        break
      case 'rss':
      case 'rss-pc':
        pcbSerials = [
          'ncmPCB',
          'mainPCB',
          'connectorPCB',
          'cloudLightPCB',
          'switchPCB',
        ]
        break
      case 'rcs':
        pcbSerials = [
          'ncmPCB',
          'mainPCB',
          'lightControlPCB',
          'lightHardware1PCB',
          'lightHardware2PCB',
          'opticalMeasurementHardware1PCB',
          'opticalMeasurementHardware2PCB',
          'internalBatterySerialPCB',
          'filterSerialPCB',
          'uiPanelCoverSerialPCB',
        ]
        break
    }
  }

  // Build initial PCB serials object with proper keys
  const initialPcbSerials: Record<string, string> = {}
  for (const key of pcbSerials) {
    initialPcbSerials[key] =
      (device?.pcbSerials as Record<string, string>)?.[key] ?? ''
  }

  const defaultValues: FormValues = {
    serial: device?.serial ?? '',
    productionDate: device?.productionDate
      ? format(parseISO(device.productionDate), 'yyyy-MM-dd')
      : '',
    productionNr: device?.productionNr ?? '',
    pcbSerials: initialPcbSerials,
    writtenOff: device?.writtenOff === true,
  }

  const getPcbLastUpdatedLabel = (key: string) => {
    const updatedAtMap = device?.pcbSerialsUpdatedAt as
      Record<string, string | undefined> | undefined
    const updatedAt = updatedAtMap?.[key]
    if (!updatedAt) return 'Last updated: -'

    const parsedDate = parseISO(updatedAt)
    const formattedDate = isValid(parsedDate)
      ? `${parsedDate.toISOString().replace('T', ' ').slice(0, 19)} UTC`
      : updatedAt

    return `Last updated: ${formattedDate}`
  }

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    values: defaultValues, // ensures reinitialize when device changes
    mode: 'onTouched', // validations are done when the user interacts with the form
  })

  const onSubmit = async (values: FormValues) => {
    if (!device) return
    setConfirmationDialog(false)
    setShowProgressIndicator(true)
    try {
      const { serial, pcbSerials, productionDate, productionNr, writtenOff } =
        values

      // Filter out undefined values and ensure we only send non-empty strings
      const cleanedPcbSerials: Record<string, string> = {}
      for (const [key, value] of Object.entries(pcbSerials)) {
        if (value && value.trim() !== '') {
          cleanedPcbSerials[key] = value.trim()
        }
      }

      const updatedDevice = await patchDevice(device, {
        serial,
        pcbSerials: cleanedPcbSerials,
        productionNr,
        productionDate: productionDate
          ? formatRFC3339(parse(productionDate, 'yyyy-MM-dd', new Date()))
          : null,
        writtenOff,
      })

      onUpdated?.(updatedDevice)
      enqueueSnackbar(t('di:device.update.succeeded'), { variant: 'success' })
    } catch {
      // handled by axios interceptor
    } finally {
      setShowProgressIndicator(false)
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(() => setConfirmationDialog(true))}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="serial"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label={t('di:device.serialNr')}
                  slotProps={{ inputLabel: { sx: labelSx } }}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={
                    !device?.editable ||
                    (device?.deliveryDate !== null &&
                      device?.deliveryDate !== undefined)
                  }
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="productionDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  variant="standard"
                  label={t('di:device.productionDate')}
                  slotProps={{ inputLabel: { sx: labelSx, shrink: true } }}
                  fullWidth
                  disabled={!device?.editable}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Controller
              name="productionNr"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="standard"
                  label={t('di:device.productionNr')}
                  slotProps={{ inputLabel: { sx: labelSx } }}
                  fullWidth
                  disabled={!device?.editable}
                />
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {pcbSerials.map((key) => (
            <Grid key={key} size={{ xs: 12, md: 3 }}>
              <Controller
                name={`pcbSerials.${key}` as const}
                control={control}
                render={({ field }) => (
                  <Tooltip
                    title={getPcbLastUpdatedLabel(key)}
                    placement="top"
                    arrow
                  >
                    <Box component="span" sx={{ display: 'block' }}>
                      <TextField
                        {...field}
                        variant="standard"
                        label={t(`di:device.${key}`)}
                        slotProps={{ inputLabel: { sx: labelSx } }}
                        fullWidth
                        disabled={!device?.editable}
                      />
                    </Box>
                  </Tooltip>
                )}
              />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Controller
              name="writtenOff"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  sx={labelSx}
                  disabled={
                    !device?.editable ||
                    (device.projectName !== null &&
                      device.projectName !== undefined)
                  }
                />
              )}
            />
          }
          label={t('di:device.writtenOff')}
          slotProps={{
            typography: {
              sx: labelSx,
            },
          }}
        />

        {device?.projectName !== null && device?.projectName !== undefined && (
          <Alert severity="info">
            {t('di:warning.disableWrittenOffForAssignedDevice')}
          </Alert>
        )}

        {device?.editable && (
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Fab
              color="primary"
              aria-label="save"
              type="submit"
              disabled={debouncedShowProgressIndicator}
            >
              <Save />
            </Fab>
          </CardActions>
        )}

        {debouncedShowProgressIndicator && (
          <LinearProgress sx={{ borderRadius: 1, mt: 2 }} />
        )}

        <ConfirmDialog
          title={t('di:device.update.title')}
          open={confirmationDialog}
          setOpen={setConfirmationDialog}
          onConfirm={handleSubmit(onSubmit)}
        >
          <Typography variant="body1">
            {t('di:device.update.explanation')}
          </Typography>
        </ConfirmDialog>
      </form>
      {/* IMEI + UID Change Dialogs */}
      <Box display="flex" gap={2} mt={2} alignItems="center">
        {(device?.type === ZKL_3000_RC ||
          device?.type === ZKL_3000_RCC ||
          device?.type === ZKL_3000) &&
          String(device?.version) === '2' &&
          (device?.projectName === null ||
            device?.projectName === undefined) && (
            <>
              <Tooltip title={t('di:device.info.imeiChange.confirmImeiChange')}>
                <Button
                  variant={'contained'}
                  color="success"
                  onClick={() => setImeiOpen(true)}
                >
                  {t('di:device.info.imeiChange.changeImeiButton')}
                </Button>
              </Tooltip>
              <ImeiChangeDialog
                open={imeiOpen}
                setOpen={setImeiOpen}
                device={device}
              />
            </>
          )}

        {device?.serial &&
          (device?.projectName === null ||
            device?.projectName === undefined) && (
            <>
              <Tooltip title={t('di:device.info.uidChange.confirmUidChange')}>
                <Button
                  variant={'contained'}
                  color="success"
                  onClick={() => setUidOpen(true)}
                >
                  {t('di:device.info.uidChange.changeUidButton')}
                </Button>
              </Tooltip>
              <UidChangeDialog
                open={uidOpen}
                setOpen={setUidOpen}
                device={device}
              />
            </>
          )}
      </Box>
    </>
  )
}
